import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, from, Observable, Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Examinees, User, Training } from '../app.urls';
 import { encryptField } from '../shared/encryption.util';
import { AppService } from './app.service';
import { CollectionService } from './collection.service';
import { WebErrorService } from './webError.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  ExpireTokenSubscription: Subscription

  constructor(private http: HttpClient, private router: Router, private appService: AppService, private collectionService: CollectionService, private webErrorService: WebErrorService,) { }

  private encryptCredentials(emailId: string, password: string): Promise<{ emailId: string; password: string }> {
    const key = environment.aesEncryptionKey;
    return Promise.all([
      encryptField(emailId, key),
      encryptField(password, key)
    ]).then(([encryptedEmailId, encryptedPassword]) => ({
      emailId: encryptedEmailId,
      password: encryptedPassword
    }));
  }

  private encryptLoginPayloadIfNeeded(payload: any): Promise<any> {
    if (!payload?.emailId || !payload?.password) {
      return Promise.resolve(payload);
    }
    return this.encryptCredentials(payload.emailId, payload.password).then((encrypted) => ({
      ...payload,
      ...encrypted
    }));
  }

  // This function is for login user
  login(userName: string, password: string): Observable<any> {
    return from(this.encryptCredentials(userName, password)).pipe(
      switchMap((loginData) => this.http.post<any>(User.Login, loginData, { 'headers': { 'content-type': 'application/json' } }).pipe(
        tap((user: any) => {
          if (user && user.data) {
            sessionStorage.currentUser = JSON.stringify(user.data);
          }
        })
      ))
    );
  }

  //Logout
  logout() {
    const UserData = JSON.parse(sessionStorage.getItem("currentUser"))
    if(this.ExpireTokenSubscription){this.ExpireTokenSubscription.unsubscribe();}
    this.ExpireTokenSubscription = this.collectionService.expireToken(UserData.useruuid, UserData.loginUuid).subscribe(res=>{
      let msg = res.message
      if(msg === "Logged out successfully"){
        sessionStorage.removeItem('currentUser');
        sessionStorage.removeItem('defaultcollectionid');
        sessionStorage.removeItem('defaultcollectionuuid')
        this.appService.changeUserHeader(0);
        this.router.navigate(['/login']);
      }
    },(error)=>{
      this.webErrorService.handleAPIErrors(error);
    });
  }


  // This funtion is for change password
  changePwd(dataToApi: any) {
    return this.http.post<any>(User.ChangePwd, JSON.stringify(dataToApi), { 'headers': { 'content-type': 'application/json' } })
  }


  // This funtion is to reset password
  resetPassword(emailId: any) {
    // const resetPwdUrl = User.ResetPassword;
    const loginData = {
      "emailId": emailId,
    };
    return this.http.post<any>(User.ResetPassword, JSON.stringify(loginData), { 'headers': { 'content-type': 'application/json' } })
  }

  getRoles() {
    return this.http.get<any>(User.Roles)
  }

  getFeatures() {
    return this.http.get<any>(User.Features)
  }

  searchRoles(roleName, roleDescription) {
    let param = '';
    if (roleName) {
      param = `?rolename=${roleName}`;
    }
    if (roleDescription) {
      param = (param != '') ? `${param}&roleDescription=${roleDescription}` : `?roleDescription=${roleDescription}`;
    }

    return this.http.get<any>(User.SearchRoles + param)
  }

  addRoles(payload) {
    return this.http.post<any>(User.addRoles, JSON.stringify(payload), { 'headers': { 'content-type': 'application/json' } })
  }

  updateRoles(payload) {
    return this.http.put<any>(User.UpdateRoles, JSON.stringify(payload), { 'headers': { 'content-type': 'application/json' } })
  }

  getRoleById(id) {
    return this.http.get<any>(User.RoleById + `${id} `)
  }

  getcreditsummary() {
    return this.http.get<any>(User.CreditsSummary)
  }

  getCredits(startDate, endDate) {
    return this.http.get<any>(User.Credits + `getcreditusagesummary?startDate=${startDate}&endDate=${endDate}`);
  }

  getpurchasehistory(startDate, endDate) {
    return this.http.get<any>(User.purchasehistory + `getpurchasehistory?startDate=${startDate}&endDate=${endDate}`);
  }

  isAuthenticated(): boolean {
    const userData = JSON.parse(sessionStorage.currentUser || null);
    return userData;
  }

  getLanguage() {
    return this.http.get<any>(User.languageSettings);
  }

  updateUserSetting(dataToApi: any) {
    return this.http.put<any>(User.updateSettings,dataToApi)
  }

  resendAuthenticationCode(payload: any) {
    return this.http.post<any>(User.resendAuthenticationCode + `?email=${payload.emailId}`, payload)
  }

  signIn(payload) {
    return from(this.encryptLoginPayloadIfNeeded(payload)).pipe(
      switchMap((encryptedPayload) => this.http.post<any>(User.signIn, encryptedPayload).pipe(
        tap((user: any) => {
          if (user && user.data) {
            sessionStorage.setItem('currentUser', JSON.stringify(user.data));
          }
        })
      ))
    );
  }

  getCaptcha() {
    return this.http.get<any>(User.Captcha);
  }

  validateCaptcha(tokenValue: any, textValue: any, inputText: any) {
    const body = new HttpParams()
    .set('DNTCaptchaText', textValue)
    .set('DNTCaptchaInputText', inputText)
    .set('DNTCaptchaToken', tokenValue)

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
      });
      return this.http.post<any>(User.ValidateCaptcha, body.toString(), { headers: headers })
  }

  checkUser(userName: string, password: string) {
    return from(this.encryptCredentials(userName, password)).pipe(
      switchMap((loginData) => this.http.post<any>(User.UserCheck, loginData, { 'headers': { 'content-type': 'application/json' } }))
    );
  }

  lockedUserList() {
    return this.http.get<any>(User.lockedUser);
  }

  unlockUser(useruuid: any) {
    return this.http.put<any>(User.unlockUser + `?useruuid=` + useruuid, null);
  }

  unlockExaminee(examineeUuid: any) {
    return this.http.put<any>(Examinees.unlockExaminee + `?examineeUuid=` + examineeUuid, null);
  }

  lockedExamineeList() {
    return this.http.get<any>(Examinees.lockedExaminee);
  }

  changePassword(payload: any) {
    return this.http.post<any>(User.changePassword, payload);
  }

  verfiyAuthCode(payload: any) {
    return this.http.post<any>(User.authcode, payload);
  }

  videoTutorial(assetGroup) {
    return this.http.get<any>(Training.tutorial + assetGroup);
  }

  authenticationCode(emailId) {
    return this.http.post<any>(User.authenticationCode + `?email=${emailId}`, '' )
  }
}
